import { useEffect, useState } from "react";
import partCodeService from "services/part-code-service";

import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

// Prime React components
import { Column } from 'primereact/column';
import "primereact/resources/themes/lara-light-cyan/theme.css";
import { TreeTable } from 'primereact/treetable';

import Dialog from "@mui/material/Dialog";

// Component for displaying part code snapshot dialog
export default function SnapshotDialog({
    open,
    onClose,
    revNum = null,
    uniqueNum = null,
    partCategoryId = null,
}) {
    const [snapshot, setSnapshot] = useState(null);
    const [recentPart, setRecentPart] = useState(null);
    const [tabValue, setTabValue] = useState("Details");
    const [controller] = useMaterialUIController();
    const APP_URL = process.env.REACT_APP_URL;
    
    // Fetch snapshot data when component mounts or revNum changes
    useEffect(() => {
        (async () => {
            console.log("Snapshot Dialog mounted with props:", { revNum, uniqueNum, partCategoryId });
            try {
                // Fetch snapshot 
                if (!uniqueNum || !partCategoryId || !revNum) {
                    return;
                }
                const response = await partCodeService.getSnapshot(uniqueNum, partCategoryId, revNum);
                setSnapshot(response.data.attributes);
                setRecentPart(response.data.recentPart || null);
                console.log("Fetched snapshot data:", response.data.attributes);
            } catch (error) {
                console.error("Error fetching snapshot data:", error);
            }
        })();
    }, [revNum]);
    
    // Formats the full part code string
    const fullPartCode = (partCode) => {
        return `E4A–${partCode.compressedPartCategory[0].categoryCode}–${partCode.unique_num.toString().padStart(5, "0")}–${partCode.revision_num}`;
    }

    // Handle tab change
    const handleChange = (event, newValue) => {
        setTabValue(newValue);
    }

    // Formats the fetched partcode's children to match the tree table's structure
    const formatPartCodeData = (raw, path = '0') => {
        if (!raw || typeof raw !== 'object') return null;

        // Distinguish between a pure part object and a relation { part, text, Quantity, _id }
        const part = raw.part && (raw.unique_num === undefined) ? raw.part : raw;
        if (!part || typeof part !== 'object') return null;

        const isRoot = path === '0';

        // Category code fallback chain
        const categoryCode =
            part.compressedPartCategory?.[0]?.categoryCode ||
            part.partCategory?.categoryCode ||
            part.partCategory || // may be an ObjectId string for root snapshot
            'UNK';

        // Build part code safely
        const buildPartCode = (p) => {
            if (!p || p.unique_num === undefined || p.revision_num === undefined) return 'Unknown Part';
            return `E4A–${categoryCode}–${String(p.unique_num).padStart(5, '0')}–${p.revision_num}`;
        };

        // Prefer a real part _id, else relation _id, else synthetic
        const key =
            part._id ||
            raw._id ||
            `${categoryCode}-${part.unique_num || 'u'}-${part.revision_num || 'r'}-${path}`;

        // Note text
        const noteText = (() => {
            const t = raw.text ?? part.text;
            const cleaned = (t || '').trim();
            if (cleaned) return cleaned;
            return isRoot ? 'Root Part Code' : '';
        })();

        // Quantity fallback
        const qty = raw.Quantity ?? part.Quantity ?? 1;

        // Link (only if we have a real _id for the part)
        const linkHref = part._id
            ? `${APP_URL}/part-code-management/view-part-code/${part._id}`
            : '#';

        // Children source:
        // Root: use compressedChildren (fully populated)
        // Non-root: use part.children (each is a relation object)
        const childSource = isRoot
            ? raw.compressedChildren
            : part.children;

        const children = Array.isArray(childSource)
            ? childSource
                .filter(c => c && typeof c === 'object')
                .map((child, idx) => formatPartCodeData(child, `${path}-${idx}`))
                .filter(Boolean)
            : [];

        return {
            key,
            data: {
                name: (
                    <a
                        href={linkHref}
                        style={{ textDecoration: 'none', color: 'blue' }}
                    >
                        {buildPartCode(part)}
                    </a>
                ),
                category: categoryCode,
                path,
                text: noteText,
                Quantity: qty
            },
            children
        };
    };

    // Format the part code data to match the nodes structure
    const nodes = snapshot?._id ? [formatPartCodeData(snapshot)] : [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ sx: { width: "500px" } }}
            sx={{
                position: "absolute",
                top: "5vh",
                left: "5%",
            }}

        >
            <ToggleButtonGroup
                color="info"
                value={tabValue}
                exclusive
                onChange={handleChange}
                aria-label="Tab Selection"
                fullWidth
            >
                <ToggleButton value="Details">Details</ToggleButton>
                <ToggleButton value="BOM">BOM</ToggleButton>
            </ToggleButtonGroup>

            {/* Details Tab */}
            {(tabValue === "Details") && (() => {

                // Sanitize value for comparison and display
                const sanitizeValue = (value) => {
                    if (value === null || value === undefined) return "";
                    if (typeof value === "string") return value.replace(/;/g, " ").trim();
                    return String(value).trim();
                };

                // Tokenize text into words and spaces
                const tokenize = (text = "") =>
                    text.split(/(\s+)/).filter((token) => token !== "");

                // Compute diff segments between old and new values
                const diffSegments = (oldValue, newValue) => {
                    const oldTokens = tokenize(oldValue);
                    const newTokens = tokenize(newValue);
                    const matrix = Array(oldTokens.length + 1)
                        .fill(null)
                        .map(() => Array(newTokens.length + 1).fill(0));

                    for (let i = 1; i <= oldTokens.length; i += 1) {
                        for (let j = 1; j <= newTokens.length; j += 1) {
                            if (oldTokens[i - 1] === newTokens[j - 1]) {
                                matrix[i][j] = matrix[i - 1][j - 1] + 1;
                            } else {
                                matrix[i][j] = Math.max(matrix[i - 1][j], matrix[i][j - 1]);
                            }
                        }
                    }

                    const segments = [];
                    let i = oldTokens.length;
                    let j = newTokens.length;

                    while (i > 0 || j > 0) {
                        if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
                            segments.unshift({ type: "equal", value: oldTokens[i - 1] });
                            i -= 1;
                            j -= 1;
                        } else if (j > 0 && (i === 0 || matrix[i][j - 1] >= matrix[i - 1][j])) {
                            segments.unshift({ type: "added", value: newTokens[j - 1] });
                            j -= 1;
                        } else {
                            segments.unshift({ type: "removed", value: oldTokens[i - 1] });
                            i -= 1;
                        }
                    }

                    return segments;
                };

                // Render the diff between old and new values
                const renderDiff = (oldValue, newValue) => {
                    const oldText = sanitizeValue(oldValue);
                    const newText = sanitizeValue(newValue);

                    if (!oldText && !newText) return "N/A";
                    if (oldText === newText) return oldText;

                    return diffSegments(oldText, newText).map((segment, idx) => {
                        if (segment.type === "added") {
                            return (
                                <span key={`added-${idx}`} style={{ color: "#2e7d32", fontWeight: 600 }}>
                                    {segment.value}
                                </span>
                            );
                        }
                        if (segment.type === "removed") {
                            return (
                                <span
                                    key={`removed-${idx}`}
                                    style={{ color: "#c62828", textDecoration: "line-through" }}
                                >
                                    {segment.value}
                                </span>
                            );
                        }
                        return (
                            <span key={`equal-${idx}`}>
                                {segment.value}
                            </span>
                        );
                    });
                };
                
                // Resolve vendor name from part object
                const resolveVendorName = (part) => {
                    if (!part?.vendors) return "";
                    const key = `vendor-${part.preferred_vendor}`;
                    return part.vendors[key] || "";
                };

                // Prepare field and attribute rows for display
                const fieldRows = [
                    { label: "Description", oldValue: snapshot?.description, newValue: recentPart?.description },
                    { label: "Manufacturer", oldValue: snapshot?.manufacturer, newValue: recentPart?.manufacturer },
                    {
                        label: "Manufacturer Part #",
                        oldValue: snapshot?.manufacturer_part_number,
                        newValue: recentPart?.manufacturer_part_number,
                    },
                    { label: "Vendor", oldValue: resolveVendorName(snapshot), newValue: resolveVendorName(recentPart) },
                    {
                        label: "Vendor Part #",
                        oldValue: snapshot?.vendor_part_number,
                        newValue: recentPart?.vendor_part_number,
                    },
                ];

                // Prepare attribute rows for display
                const oldAttributes = snapshot?.attributes ?? {};
                const newAttributes = recentPart?.attributes ?? {};
                const attributeRows = Array.from(
                    new Set([...Object.keys(oldAttributes), ...Object.keys(newAttributes)])
                )
                    .map((key) => {
                        const oldValue = oldAttributes[key];
                        const newValue = newAttributes[key];
                        if (!sanitizeValue(oldValue) && !sanitizeValue(newValue)) return null;
                        return { key, oldValue, newValue };
                    })
                    .filter(Boolean);

                return (
                    <MDBox p={3}>
                        <MDBox textAlign="center" mb={3}>
                            <MDTypography variant="h3" fontWeight="medium">
                                {snapshot ? fullPartCode(snapshot) : ""}
                            </MDTypography>
                        </MDBox>

                        {fieldRows.map((row) => (
                            <Grid container spacing={{ xs: 2, md: 4 }} mb={1} key={row.label}>
                                <Grid item xs={12} md={6}>
                                    <MDTypography
                                        variant="h6"
                                        fontWeight="medium"
                                        textAlign={{ xs: "left", md: "right" }}
                                    >
                                        {row.label}
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <MDTypography variant="h6" fontWeight="regular" component="div">
                                        {row.label === "Description"
                                            ? sanitizeValue(row.newValue) || sanitizeValue(row.oldValue) || "N/A"
                                            : renderDiff(row.oldValue, row.newValue)}
                                    </MDTypography>
                                </Grid>
                            </Grid>
                        ))}

                        {attributeRows.length > 0 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <MDBox>
                                    <MDBox width="100%" overflow="auto">
                                        <Table sx={{ maxWidth: "30rem" }}>
                                            <MDBox component="thead">
                                                <TableRow>
                                                    <MDBox
                                                        component="th"
                                                        width={{ xs: "50%", md: "50%" }}
                                                        py={1.5}
                                                        px={1}
                                                        textAlign="left"
                                                        sx={{ borderBottom: "1px solid rgba(0,0,0,0.12)" }}
                                                    >
                                                        <MDTypography variant="h6" color="text" fontWeight="medium">
                                                            Attributes
                                                        </MDTypography>
                                                    </MDBox>
                                                    <MDBox
                                                        component="th"
                                                        py={1.5}
                                                        pl={3}
                                                        pr={1}
                                                        textAlign="left"
                                                        sx={{ borderBottom: "1px solid rgba(0,0,0,0.12)" }}
                                                    >
                                                        <MDTypography variant="h6" color="text" fontWeight="medium">
                                                            Changes
                                                        </MDTypography>
                                                    </MDBox>
                                                </TableRow>
                                            </MDBox>
                                            <TableBody>
                                                {attributeRows.map(({ key, oldValue, newValue }) => (
                                                    <TableRow key={key}>
                                                        <MDBox
                                                            component="td"
                                                            py={1}
                                                            px={1}
                                                            textAlign="left"
                                                            sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                                                        >
                                                            <MDTypography variant="button" fontWeight="medium">
                                                                {key}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <MDBox
                                                            component="td"
                                                            py={1}
                                                            pl={3}
                                                            pr={1}
                                                            textAlign="left"
                                                            sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                                                        >
                                                            <MDTypography variant="button" fontWeight="regular" component="div">
                                                                {renderDiff(oldValue, newValue)}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </MDBox>
                                </MDBox>
                            </>
                        )}
                    </MDBox>
                );
            })()}
            {/* Bill of Materials Tab */}
            {(tabValue === "BOM") && (
                <MDBox p={.5}>
                    <MDTypography variant="h3" fontWeight="medium" textAlign="center">
                        Bill Of Materials
                    </MDTypography>
                    {/* TreeTable */}
                    <TreeTable
                        value={nodes}
                        className="p-datatable-sm"
                        showGridlines
                        tableStyle={{ minWidth: '22rem' }}
                        pt={{
                            root: { style: { borderRadius: '8px' } },
                            header: { style: { padding: '.5rem' } },
                            wrapper: { style: { padding: '.5rem' } }
                        }}

                    >   {/*Columns*/}
                        <Column
                            field="name"
                            header=" Part Code"
                            expander
                            style={{ width: '51%', paddingLeft: '.5rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                        />
                        <Column
                            field="Quantity"
                            header="Qty"
                            style={{ width: '12%', paddingLeft: '.5rem' }}
                        />
                        <Column
                            field="text"
                            header="Notes"
                            style={{ width: '37%', paddingLeft: '.5rem' }}
                        />
                    </TreeTable>
                </MDBox>
            )}
        </Dialog >
    )
}